import axios, { AxiosResponse, isAxiosError } from "axios";
import { Paginated, RequestError, setFile } from "./api";

const url = `${process.env.REACT_APP_URL}/gistm`;

export interface GISTMRequisitos {
  id: number;
  idBarragem: number;
  principio: number; //
  requisito: number; //
  desc: string; //
  aplicacao: boolean; //
  documentacao: "sim" | "parcial" | "inexistente" | "não necessário"; //
  peso: number; //
  obsHbr?: string; //
  obsCliente?: string; //
  idUpdate: string;
  dataCadastro: Date;
  dataModificacao?: Date;
}

export interface GISTMCheckFiles {
  id: number;
  idChecklist: number;
  file: string;
  downloadFile: string;
  filename: string;
  fileExt: string;
  fileSize: number;
}

export interface GISTMChecklist {
  id: number; //
  idBarragem: number; //
  requisito: number; //
  principio: number; //
  title: string;
  desc: string;
  checked: boolean;
  idUpdate: string; //
  dataCadastro: Date; //
  dataModificacao?: Date; //
  files: GISTMCheckFiles[]; //
}

// GISTM Requisitos

const addRequisito = async (
  idBarragem: number,
  requisito: Omit<
    GISTMRequisitos,
    "id" | "idBarragem" | "dataCadastro" | "dataModificacao" | "idUpdate"
  >
): Promise<GISTMRequisitos | RequestError> => {
  try {
    const data: AxiosResponse<GISTMRequisitos> = await axios.post(
      `${url}/requisito/${idBarragem}`,
      { ...requisito }
    );

    return processData<GISTMRequisitos>(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Requisito Error",
        message:
          error.response?.data.message ?? "Erro ao adicionar este requisito",
      };
    } else {
      throw error;
    }
  }
};

const getAllRequisitos = async (
  idBarragem: number,
  page: number,
  perPage: number
): Promise<Paginated<GISTMRequisitos> | RequestError> => {
  try {
    const data: AxiosResponse<Paginated<GISTMRequisitos>> = await axios.get(
      `${url}/requisito/all/${idBarragem}/${page}/${perPage}`
    );

    data.data.items = data.data.items.map(processData<GISTMRequisitos>);

    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Requisito Error",
        message: error.response?.data.message ?? "Erro ao obter requisitos",
      };
    } else {
      throw error;
    }
  }
};

const getRequisitoById = async (
  idRequisito: number
): Promise<GISTMRequisitos | RequestError> => {
  try {
    const data: AxiosResponse<GISTMRequisitos> = await axios.get(
      `${url}/requisito/${idRequisito}`
    );

    return processData<GISTMRequisitos>(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Requisito Error",
        message: error.response?.data.message ?? "Erro ao obter requisito",
      };
    } else {
      throw error;
    }
  }
};

const updateRequisito = async (
  requisito: Omit<
    GISTMRequisitos,
    "idBarragem" | "dataCadastro" | "dataModificacao" | "idUpdate"
  >
): Promise<GISTMRequisitos | RequestError> => {
  try {
    const data: AxiosResponse<GISTMRequisitos> = await axios.put(
      `${url}/requisito/${requisito.id}`,
      { ...requisito }
    );

    return processData<GISTMRequisitos>(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Requisito Error",
        message: error.response?.data.message ?? "Erro ao atualizar requisito",
      };
    } else {
      throw error;
    }
  }
};

const removeRequisito = async (
  id: number
): Promise<GISTMRequisitos | RequestError> => {
  try {
    const data: AxiosResponse<GISTMRequisitos> = await axios.delete(
      `${url}/requisito/${id}`
    );

    return processData<GISTMRequisitos>(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Requisito Error",
        message: error.response?.data.message ?? "Erro ao excluir requisito",
      };
    } else {
      throw error;
    }
  }
};

// GISTM Checklist

const addChecklist = async (
  idBarragem: number,
  checklist: Omit<
    GISTMChecklist,
    | "id"
    | "idBarragem"
    | "dataCadastro"
    | "dataModificacao"
    | "idUpdate"
    | "files"
  >
): Promise<GISTMChecklist | RequestError> => {
  try {
    const data: AxiosResponse<GISTMChecklist> = await axios.post(
      `${url}/checklist/${idBarragem}`,
      { ...checklist }
    );

    return processData<GISTMChecklist>(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Checklist Error",
        message:
          error.response?.data.message ??
          "Erro ao adicionar este item ao checklist",
      };
    } else {
      throw error;
    }
  }
};

const getAllChecklist = async (
  idBarragem: number,
  page: number,
  perPage: number
): Promise<Paginated<GISTMChecklist> | RequestError> => {
  try {
    const data: AxiosResponse<Paginated<GISTMChecklist>> = await axios.get(
      `${url}/checklist/all/${idBarragem}/${page}/${perPage}`
    );

    data.data.items = data.data.items.map(processData<GISTMChecklist>);

    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Checklist Error",
        message:
          error.response?.data.message ?? "Erro ao obter lista de checklist",
      };
    } else {
      throw error;
    }
  }
};

const getChecklistById = async (
  id: number
): Promise<GISTMChecklist | RequestError> => {
  try {
    const data: AxiosResponse<GISTMChecklist> = await axios.get(
      `${url}/checklist/${id}`
    );

    return processData<GISTMChecklist>(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Checklist Error",
        message:
          error.response?.data.message ?? "Erro ao obter item do checklist",
      };
    } else {
      throw error;
    }
  }
};

const updateChecklist = async (
  checklist: Omit<
    GISTMChecklist,
    "idBarragem" | "dataCadastro" | "dataModificacao" | "idUpdate" | "files"
  >
): Promise<GISTMChecklist | RequestError> => {
  try {
    const data: AxiosResponse<GISTMChecklist> = await axios.put(
      `${url}/checklist/${checklist.id}`,
      { ...checklist }
    );

    return processData<GISTMChecklist>(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Checklist Error",
        message:
          error.response?.data.message ?? "Erro ao atualizar item do Checklist",
      };
    } else {
      throw error;
    }
  }
};

const removeChecklist = async (
  id: number
): Promise<GISTMChecklist | RequestError> => {
  try {
    const data: AxiosResponse<GISTMChecklist> = await axios.delete(
      `${url}/checklist/${id}`
    );

    return processData<GISTMChecklist>(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Checklist Error",
        message: error.response?.data.message ?? "Erro ao excluir Checklist",
      };
    } else {
      throw error;
    }
  }
};

const setChecklistFile = async (
  idChecklist: number,
  file: File,
  onProgess?: (percent: number) => void
): Promise<GISTMCheckFiles | RequestError> => {
  return setFile<GISTMCheckFiles>(
    `${url}/checklist/file_upload_parts/${idChecklist}`,
    `${url}/checklist/file_upload_complete`,
    file,
    onProgess
  );
};

const removeChecklistFile = async (
  id: number
): Promise<GISTMCheckFiles | RequestError> => {
  try {
    const data: AxiosResponse<GISTMCheckFiles> = await axios.delete(
      `${url}/checklist/removefile/${id}`
    );

    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Checkfile Error",
        message: error.response?.data.message ?? "Erro ao excluir Arquivo do Checklist",
      };
    } else {
      throw error;
    }
  }
};

type ProcessType = GISTMRequisitos | GISTMChecklist;

function processData<T extends ProcessType>(value: T) {
  value.dataCadastro = new Date(value.dataCadastro);
  value.dataModificacao &&= new Date(value.dataModificacao);
  return value as T;
}

export const ApiGISTM = {
  addRequisito,
  getAllRequisitos,
  getRequisitoById,
  updateRequisito,
  removeRequisito,
  addChecklist,
  getAllChecklist,
  getChecklistById,
  updateChecklist,
  removeChecklist,
  setChecklistFile,
  removeChecklistFile,
};
